import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { 
  Loader2, FileText, Check, X, Clock, Building2, Mail, Phone, MapPin, 
  DollarSign, Globe, Trash2, ChevronDown, ChevronUp, Crown, Trophy, Shield, Star
} from "lucide-react";

const SponsorApplicationsManager = () => {
  const { authAxios } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedApp, setExpandedApp] = useState(null);

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      const response = await authAxios.get('/sponsor-applications');
      setApplications(response.data);
    } catch (e) {
      toast.error("Failed to fetch applications");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (id, status) => {
    try {
      await authAxios.put(`/sponsor-applications/${id}/status?status=${status}`);
      toast.success(`Application ${status}`);
      fetchApplications();
    } catch (e) {
      toast.error("Failed to update status");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this application?")) return;
    
    try {
      await authAxios.delete(`/sponsor-applications/${id}`);
      toast.success("Application deleted");
      fetchApplications();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const getTierIcon = (tier) => {
    switch (tier) {
      case 'platinum': return { icon: Crown, color: 'text-cyan-400' };
      case 'gold': return { icon: Trophy, color: 'text-yellow-400' };
      case 'silver': return { icon: Shield, color: 'text-gray-400' };
      default: return { icon: Star, color: 'text-amber-600' };
    }
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'approved': return { color: 'bg-neon-green/20 text-neon-green border-neon-green/50', icon: Check };
      case 'rejected': return { color: 'bg-red-500/20 text-red-500 border-red-500/50', icon: X };
      default: return { color: 'bg-yellow-500/20 text-yellow-500 border-yellow-500/50', icon: Clock };
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' 
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-neon-pink animate-spin" />
      </div>
    );
  }

  const pendingCount = applications.filter(a => a.status === 'pending').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="font-orbitron text-xl text-white flex items-center gap-2">
          <FileText className="w-5 h-5 text-neon-pink" />
          Sponsor Applications
          {pendingCount > 0 && (
            <span className="px-2 py-0.5 bg-yellow-500/20 border border-yellow-500/50 text-yellow-500 font-orbitron text-xs">
              {pendingCount} PENDING
            </span>
          )}
        </h2>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="cyber-card p-4 text-center border-yellow-500/30">
          <Clock className="w-6 h-6 text-yellow-500 mx-auto mb-2" />
          <p className="font-orbitron text-2xl text-white">{applications.filter(a => a.status === 'pending').length}</p>
          <p className="font-rajdhani text-xs text-gray-500">Pending</p>
        </div>
        <div className="cyber-card p-4 text-center border-neon-green/30">
          <Check className="w-6 h-6 text-neon-green mx-auto mb-2" />
          <p className="font-orbitron text-2xl text-white">{applications.filter(a => a.status === 'approved').length}</p>
          <p className="font-rajdhani text-xs text-gray-500">Approved</p>
        </div>
        <div className="cyber-card p-4 text-center border-red-500/30">
          <X className="w-6 h-6 text-red-500 mx-auto mb-2" />
          <p className="font-orbitron text-2xl text-white">{applications.filter(a => a.status === 'rejected').length}</p>
          <p className="font-rajdhani text-xs text-gray-500">Rejected</p>
        </div>
      </div>

      {/* Applications List */}
      <div className="space-y-4">
        {applications.map(app => {
          const tierConfig = getTierIcon(app.sponsorship_tier);
          const TierIcon = tierConfig.icon;
          const statusConfig = getStatusConfig(app.status);
          const StatusIcon = statusConfig.icon;
          const isExpanded = expandedApp === app.id;

          return (
            <div key={app.id} className={`cyber-card overflow-hidden ${app.status === 'pending' ? 'border-yellow-500/30' : ''}`}>
              {/* Header */}
              <div 
                className="p-4 cursor-pointer hover:bg-white/5 transition-colors"
                onClick={() => setExpandedApp(isExpanded ? null : app.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`p-2 ${tierConfig.color.replace('text-', 'bg-')}/10`}>
                      <TierIcon className={`w-5 h-5 ${tierConfig.color}`} />
                    </div>
                    <div>
                      <h3 className="font-orbitron text-sm text-white">{app.business_name}</h3>
                      <p className="font-rajdhani text-xs text-gray-500">{app.contact_name} • {app.email}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <span className="font-orbitron text-lg text-neon-green">${app.donation_amount.toLocaleString()}</span>
                    <span className={`px-2 py-1 border text-xs font-orbitron uppercase ${statusConfig.color}`}>
                      {app.status}
                    </span>
                    {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
                  </div>
                </div>
              </div>

              {/* Expanded Details */}
              {isExpanded && (
                <div className="border-t border-white/10 p-4 bg-white/5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Business Info */}
                    <div className="space-y-3">
                      <h4 className="font-orbitron text-xs text-neon-blue flex items-center gap-2">
                        <Building2 className="w-4 h-4" />
                        BUSINESS DETAILS
                      </h4>
                      <div className="space-y-2 text-sm">
                        <p className="font-rajdhani text-gray-400">
                          <span className="text-gray-600">Business:</span> {app.business_name}
                        </p>
                        {app.website && (
                          <p className="font-rajdhani text-gray-400 flex items-center gap-2">
                            <Globe className="w-3 h-3" />
                            <a href={app.website} target="_blank" rel="noopener noreferrer" className="text-neon-blue hover:underline">
                              {app.website}
                            </a>
                          </p>
                        )}
                        <p className="font-rajdhani text-gray-400 flex items-center gap-2">
                          <MapPin className="w-3 h-3" />
                          {app.business_address}, {app.city}, {app.state} {app.zip_code}
                        </p>
                      </div>
                    </div>

                    {/* Contact Info */}
                    <div className="space-y-3">
                      <h4 className="font-orbitron text-xs text-neon-green flex items-center gap-2">
                        <Mail className="w-4 h-4" />
                        CONTACT INFO
                      </h4>
                      <div className="space-y-2 text-sm">
                        <p className="font-rajdhani text-gray-400">
                          <span className="text-gray-600">Contact:</span> {app.contact_name}
                        </p>
                        <p className="font-rajdhani text-gray-400 flex items-center gap-2">
                          <Mail className="w-3 h-3" />
                          <a href={`mailto:${app.email}`} className="text-neon-blue hover:underline">{app.email}</a>
                        </p>
                        {app.phone && (
                          <p className="font-rajdhani text-gray-400 flex items-center gap-2">
                            <Phone className="w-3 h-3" />
                            {app.phone}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Donation Info */}
                    <div className="space-y-3">
                      <h4 className="font-orbitron text-xs text-neon-pink flex items-center gap-2">
                        <DollarSign className="w-4 h-4" />
                        SPONSORSHIP DETAILS
                      </h4>
                      <div className="space-y-2 text-sm">
                        <p className="font-rajdhani text-gray-400">
                          <span className="text-gray-600">Tier:</span> <span className={`uppercase ${tierConfig.color}`}>{app.sponsorship_tier}</span>
                        </p>
                        <p className="font-rajdhani text-gray-400">
                          <span className="text-gray-600">Amount:</span> <span className="text-neon-green font-orbitron">${app.donation_amount.toLocaleString()}</span>
                        </p>
                        <p className="font-rajdhani text-gray-400">
                          <span className="text-gray-600">Payment:</span> {app.payment_method.replace('_', ' ')}
                        </p>
                      </div>
                    </div>

                    {/* Message */}
                    {app.message && (
                      <div className="space-y-3">
                        <h4 className="font-orbitron text-xs text-yellow-400">MESSAGE</h4>
                        <p className="font-rajdhani text-sm text-gray-400 bg-black/30 p-3 border border-white/10">
                          "{app.message}"
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Meta & Actions */}
                  <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between">
                    <p className="font-rajdhani text-xs text-gray-600">
                      Submitted: {formatDate(app.created_at)}
                    </p>
                    
                    <div className="flex items-center gap-2">
                      {app.status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleStatusUpdate(app.id, 'approved')}
                            className="cyber-btn py-2 px-4 text-xs border-neon-green text-neon-green hover:bg-neon-green hover:text-black"
                          >
                            <Check className="w-4 h-4 mr-1 inline" />
                            APPROVE
                          </button>
                          <button
                            onClick={() => handleStatusUpdate(app.id, 'rejected')}
                            className="cyber-btn py-2 px-4 text-xs border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                          >
                            <X className="w-4 h-4 mr-1 inline" />
                            REJECT
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => handleDelete(app.id)}
                        className="p-2 text-gray-500 hover:text-red-500 hover:bg-red-500/10 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {applications.length === 0 && (
        <div className="text-center py-12 cyber-card">
          <FileText className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <p className="font-rajdhani text-gray-500">No sponsor applications yet.</p>
        </div>
      )}
    </div>
  );
};

export default SponsorApplicationsManager;
